import api, {
    Borrower, DocumentType, LoanDetail, OCRFieldAction
} from '@api';
import { Check, HourglassBottom } from '@mui/icons-material';
import {
    Alert, DialogContent, List, Paper, Tooltip
} from '@mui/material';
import {
    Button, DialogActions, ExpandableHeader, Loader, RoutedDialog, RoutedDialogImplProps
} from '@tsp-ui/core/components';
import { useAsyncEffect, usePageMessage } from '@tsp-ui/core/utils';
import { useGetCurrentAccount } from '@utils';
import { DocumentLink } from '@views/components/DocumentLink';
import { IconMenuItem } from '@views/components/IconMenuItem';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { useCallback, useContext, useState } from 'react';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import { LoanDetailContext } from '../LoanDetailPage';
import { LoansContext } from '../LoansPage';
import { PendingLoanDetailContext } from '../PendingLoanDetailPage';

import styles from './OCRReviewDialog.module.scss';
import {
    OCRDataReviewFormValues, OCRReviewForm, isSameBorrower, ocrReviewFormIsIncomplete
} from './OCRReviewForm';
import { RegisteredLoanCardContext } from './RegisteredLoanCard';


interface OCRDataReviewLocationState {
    documentId: string;
}

export function OCRReviewDialog(props: RoutedDialogImplProps) {
    const { loanDetail: loanDetailFromLoanDetailContext } = useContext(LoanDetailContext) || {};
    const { loanDetail: loanDetailFromPendingLoanDetailContext } = useContext(PendingLoanDetailContext) || {};
    const loanDetailFromContext = loanDetailFromLoanDetailContext || loanDetailFromPendingLoanDetailContext;
    const {
        availableDocs: availableDocsFromContext, requiredDocCodes: requiredDocCodesFromContext
    } = useContext(LoansContext);
    const { loanDocuments, setLoanDocuments } = useContext(RegisteredLoanCardContext);

    const needToFetchDocs = !availableDocsFromContext || !requiredDocCodesFromContext;

    const { id: clientID, customerId } = useGetCurrentAccount();
    const pageMessage = usePageMessage();
    const navigate = useNavigate();
    const { documentId } = (useLocation().state as OCRDataReviewLocationState) || {
        documentId: ''
    };
    const formMethods = useForm<OCRDataReviewFormValues>();
    const formValues = formMethods.watch();

    // Custom match string required because path uses a literal loan ID instead of a placeholder.
    const { loanID = '' } = useMatch('/accounts/:clientID/loans/:loanID/document-ocr-review')?.params || { loanID: '' };

    const [ loading, setLoading ] = useState(true);
    const [ saveLoading, setSaveLoading ] = useState(false);
    const [ loanDetail, setLoanDetail ] = useState<LoanDetail | undefined>(loanDetailFromContext);
    const [ requiredDocCodes, setRequiredDocCodes ] = useState<string[]>(requiredDocCodesFromContext || []);
    const [ availableDocs, setAvailableDocs ] = useState<DocumentType[]>(availableDocsFromContext || []);
    const [ ocrData, setOcrData ] = useState<Partial<LoanDetail>>({});
    const [ documentUrl, setDocumentUrl ] = useState('');

    const requiredLoanDocuments = availableDocs
        ?.filter(doc => requiredDocCodes.includes(doc.code))
        .map(doc => loanDocuments?.find(uploadedDoc => uploadedDoc.name === doc.name)!)
        .filter(Boolean);

    const original = loanDetail;
    const updated = !documentId ? loanDetail : {
        ...loanDetail!,
        ...ocrData
    };
    const currentDocument = loanDocuments?.find(({ id }) => id === documentId);
    const disableSubmit = currentDocument?.isReviewed || ocrReviewFormIsIncomplete(formValues);

    // effect to get loan detail
    useAsyncEffect(useCallback(async () => {
        if (!loanDetail) {
            try {
                setLoanDetail(await api.loans.getLoanDetail(clientID, loanID, customerId));
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching loan details', error);
            }
        }
    }, [
        loanDetail, clientID, loanID, customerId, pageMessage
    ]));

    // effect to get document and OCR data
    useAsyncEffect(useCallback(async () => {
        if (loanDetail) {
            try {
                if (needToFetchDocs) {
                    setAvailableDocs(await api.documentType.getAvailableDocTypes(clientID));
                    setRequiredDocCodes(await api.documentType.getRequiredDocCodes(clientID));
                }

                const ocrData = await api.loans.document.getOCRDataForDocument(
                    clientID, loanID, documentId, customerId
                );

                setOcrData(ocrData);

                if (currentDocument?.isReviewed) {
                    const ocrSubmissionData = await api.loans.document.getOCRSubmissionDataForDocument(
                        clientID, loanID, documentId, customerId
                    );

                    if (ocrSubmissionData) {
                        formMethods.reset(ocrSubmissionData);

                        setLoading(false);

                        return;
                    }
                }

                formMethods.reset(generateOCRDataReviewFormValues(ocrData, loanDetail));
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching loan documents', error);
            }

            setLoading(false);
        }
    }, [
        needToFetchDocs, clientID, loanID, documentId, customerId,
        currentDocument?.isReviewed, formMethods, loanDetail, pageMessage
    ]));

    // effect to get document url
    useAsyncEffect(useCallback(async () => {
        if (!loanID) {
            return;
        }

        setDocumentUrl(
            (await api.loans.document.getLoanDocumentUrl(clientID, loanID, documentId, customerId)).preSignedUrl
        );
    }, [
        clientID, loanID, documentId, customerId
    ]));

    const handleSubmit = formMethods.handleSubmit(
        async (values) => {
            setSaveLoading(true);

            try {
                await api.loans.document.updateOCRSubmissionDataForDocument(
                    clientID, loanID, documentId, values, customerId
                );

                setLoanDetail(await api.loans.getLoanDetail(clientID, loanID, customerId));

                setLoanDocuments(prevDocs => {
                    const updatedDocs = prevDocs?.map(doc => {
                        if (doc.id === documentId) {
                            return {
                                ...doc,
                                isReviewed: true
                            };
                        }
                        return doc;
                    });

                    return updatedDocs;
                });

                // navigate to next pending document
                const nextDocument = requiredLoanDocuments?.find(doc => doc.id !== documentId && !doc.isReviewed);
                if (nextDocument) {
                    navigate('', {
                        state: {
                            documentId: nextDocument.id
                        }
                    });
                } else {
                    navigate(`/accounts/${clientID}/loans/${loanID}`);
                }

                pageMessage.success('OCR data submitted successfully');
            } catch (error) {
                pageMessage.handleApiError('An error occurred while updating document data', error);
            }

            setSaveLoading(false);
        }
    );

    function approveOrRejectAll(action: OCRFieldAction) {
        Object.keys(formValues).forEach((fieldName) => {
            if (fieldName === 'borrowers') {
                formValues.borrowers!.forEach((borrower, borrowerIndex) => {
                    Object.keys(borrower).forEach(borrowerField => {
                        if (borrowerField === 'id' || borrowerField === 'isNewBorrower') {
                            return;
                        }

                        formMethods.setValue(
                            `borrowers.${borrowerIndex}.${borrowerField}.action` as FieldPath<OCRDataReviewFormValues>,
                            action
                        );
                    });
                });
            } else {
                formMethods.setValue(`${fieldName as keyof LoanDetail}.action` as FieldPath<OCRDataReviewFormValues>, action);
            }
        });
    }

    return (
        <RoutedDialog
            {...props}
            transitionDuration={0}
            saveLoading={saveLoading}
            title={(
                <div className={styles.title}>
                    Review Document Data -&nbsp;

                    {currentDocument && (
                        <DocumentLink
                            variant="h6"
                            document={currentDocument}
                            name={currentDocument.name || ''}
                        />
                    )}
                </div>
            )}
            maxWidth={false}
            classes={{
                paper: clsx(styles.dialogPaper, {
                    [styles.isDocument]: true
                })
            }}
        >
            <DialogContent className={styles.documentDialogContent}>
                <Paper
                    className={styles.documentContentContainer}
                    elevation={0}
                >
                    <List disablePadding>
                        {requiredLoanDocuments?.map((document) => {
                            const maxLength = 28;
                            const isTruncated = document.name.length > maxLength;
                            const displayName = isTruncated
                                ? `${document.name.substring(0, maxLength)}...`
                                : document.name;

                            return (
                                <Tooltip title={isTruncated ? document.name : ''}>
                                    <div>
                                        <IconMenuItem
                                            key={document.id}
                                            title={displayName}
                                            to=""
                                            state={{
                                                documentId: document.id
                                            }}
                                            selected={document.id === documentId}
                                            classes={{
                                                root: styles.menuItem,
                                                selected: styles.selected
                                            }}
                                            icon={document.isReviewed ? (
                                                <Tooltip title="Reviewed">
                                                    <Check
                                                        color="success"
                                                        fontSize="small"
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Needs Review">
                                                    <HourglassBottom
                                                        fontSize="small"
                                                        color="primary"
                                                    />
                                                </Tooltip>
                                            )}
                                        />
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </List>

                    <Paper
                        elevation={0}
                        className={styles.documentDataContainer}
                    >
                        {loading ? <Loader loading /> : (
                            <Paper
                                elevation={0}
                                className={styles.documentDataPaper}
                            >
                                {currentDocument?.isReviewed && (
                                    <Alert
                                        icon={<Check fontSize="inherit" />}
                                        severity="success"
                                    >
                                        This document has already been reviewed and submitted.
                                    </Alert>
                                )}

                                <ExpandableHeader
                                    disableExpand={false}
                                    title="Data Differences"
                                    secondaryText={(
                                        <div className={styles.actionButtons}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => approveOrRejectAll(OCRFieldAction.REJECT)}
                                                classes={{ root: styles.rejectedText }}
                                            >
                                                Reject All
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => approveOrRejectAll(OCRFieldAction.APPROVE)}
                                                classes={{ root: styles.approvedText }}
                                            >
                                                Approve All
                                            </Button>
                                        </div>
                                    )}
                                    defaultExpand
                                    expandedContent={(
                                        <FormProvider {...formMethods}>
                                            <OCRReviewForm
                                                original={original}
                                                updated={updated}
                                                handleSubmit={handleSubmit}
                                            />
                                        </FormProvider>
                                    )}
                                />

                                <ExpandableHeader
                                    disableExpand={false}
                                    title="Document Preview"
                                    secondaryText=""
                                    expandedContent={(
                                        <div className={styles.expandedContent}>
                                            <iframe
                                                title={currentDocument?.name}
                                                className={styles.iframe}
                                                src={documentUrl}
                                            />
                                        </div>
                                    )}
                                />
                            </Paper>
                        )}
                    </Paper>
                </Paper>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    type="submit"
                    form="ocr-review-form"
                    disabled={disableSubmit || loading}
                    tooltip={disableSubmit ? currentDocument?.isReviewed ? 'ADE Data has already been submitted for this document' : 'Please review all fields' : ''}
                >
                    Submit
                </Button>
            </DialogActions>
        </RoutedDialog>
    );
}

function generateOCRDataReviewFormValues(
    ocrData: Partial<LoanDetail>,
    original?: LoanDetail
): OCRDataReviewFormValues {
    const result: OCRDataReviewFormValues = {};

    Object.entries(ocrData).forEach(([ field, value ]) => {
        const fieldName = field as keyof LoanDetail;

        if (fieldName === 'id' || fieldName === 'losLoanId') {
            return; // don't include these fields
        }

        if (fieldName === 'borrowers') {
            const borrowers = value as Borrower[];

            result.borrowers = borrowers.map((borrower) => {
                const borrowerResult: NonNullable<OCRDataReviewFormValues['borrowers']>[number] = { id: borrower.id! };

                const originalBorrower = original?.borrowers.find(b => isSameBorrower(b, borrower));

                if (!originalBorrower) {
                    borrowerResult.isNewBorrower = true;
                } else if (deepEqual(originalBorrower, borrower)) {
                    return borrowerResult; // don't include anything unless borrower info has changed from the original
                }

                Object.entries(borrower).forEach(([ field, value ]) => {
                    if (field === 'isNewBorrower') {
                        return;
                    }

                    if (field === 'id') {
                        borrowerResult.id = value;
                        return;
                    }

                    const fieldName = field as keyof Omit<Borrower, 'id'>;

                    if (originalBorrower && deepEqual(originalBorrower[fieldName], value)) {
                        return; // don't include any fields that haven't changed
                    } else {
                        borrowerResult[fieldName] = {
                            action: '' as OCRFieldAction
                        };
                    }
                });

                return borrowerResult;
            });

            return;
        }

        if (original && (deepEqual(original[fieldName], value) || original[fieldName] === value)) {
            return;
        } else {
            result[fieldName] = {
                action: '' as OCRFieldAction
            };
        }
    });

    return result;
}
